// Imports
import { Add, ArrowBack, MenuBookOutlined } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getHubSelection } from '../../features/hubSelection';
import { GobikesApi } from '../../config/Apis';
import { parseErrorForAxios } from '../utils/CommonUtils';
import { getAuthCookie } from '../auth/AuthUtil';
import { HubBikeModel } from '../utils/Models';
import { BikesTable } from './BikeTable';
import { BikeDetails } from './BikeDetails';
import { Colors, DefaultSliceData, DEFAULT_LOADER_STYLES } from '../../config/Constants';
import { DashboardLoader } from '../utils/LocalStateUtils';
import { setNavSelection } from '../../features/navSelection';

// Types and constants
type InventoryView = 'all' | 'individual' | 'create';
const defaultView = 'all';
interface LocalState {
  loading?: boolean;
  error?: boolean;
  message?: string;
  view: InventoryView;
  bikeData?: HubBikeModel;
}

// Main app component
export function Inventory(props: {
  window?: () => Window;
  drawerWidth: number
}) {
  // Local state
  const [localState, setLocalState] = useState({ view: defaultView as InventoryView } as LocalState);
  const [bikesData, setBikesData] = useState<HubBikeModel[]>([]);
  const [pendingAction, setPendingAction] = useState<'discount' | 'photos' | undefined>(undefined);

  // Local variables based on state
  const bikeDetailsView = localState.view === 'create' || (localState.view === 'individual' && localState.bikeData);

  // Global state
  const selectedHub = useSelector(getHubSelection);
  const authCookie = getAuthCookie();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const deepLinkBikeId = searchParams.get('bikeId') ? Number(searchParams.get('bikeId')) : null;
  const deepLinkAction = searchParams.get('action') as 'discount' | 'photos' | null;

  // Set nav state
  useEffect(() => {
    dispatch(setNavSelection(4));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load bikes on first load
  const loadBikes = () => {
    const authCookie = getAuthCookie();
    if (!selectedHub?.id || !authCookie) return;
    setLocalState({ loading: true, view: defaultView });
    (async () => {
      try {
        const { data } = await GobikesApi.get(`/vendors/hubs/${selectedHub.id}/bikes`, { headers: { 'vendor-token': authCookie }, params: { hideBlocked: true, overrides: true, loadRatings: true } })
        const bikesList: HubBikeModel[] = data.data;
        setBikesData(bikesList);

        // Deep link from Analytics "Underperforming Vehicles" section: jump straight to that vehicle
        const targetBike = deepLinkBikeId ? bikesList.find(b => b.bikeId === deepLinkBikeId) : undefined;
        if (targetBike) {
          setLocalState({ view: 'individual', bikeData: targetBike });
          setPendingAction(deepLinkAction || undefined);
          navigate('/bikes', { replace: true });
        } else {
          setLocalState({ view: defaultView });
        }
      } catch (e) {
        setLocalState({ error: true, message: parseErrorForAxios(e), view: defaultView });
      }
    })();
  }
  useEffect(loadBikes, [selectedHub]); // eslint-disable-line react-hooks/exhaustive-deps

  // Utility function to activate or activate bike
  const setBikeActive = (bikeId: number, vendorActive: boolean) => {

    // Parse input and check for errors
    const payload = { vendorActive };

    // Set loading mode and try to save bike
    setLocalState({ loading: true, view: defaultView });
    (async () => {
      try {
        await GobikesApi.put(`/vendors/hubs/${selectedHub.id}/bikes/${bikeId}/active`, payload, { headers: { 'vendor-token': authCookie } });
        setLocalState({ loading: false, view: defaultView })
        loadBikes();
      } catch (e: any) {
        const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
        setLocalState({ error: true, message: errorMessage, loading: false, view: defaultView })
      }
    })();
  }

  // Loading view
  let mainContent = <div></div>;
  if (localState.loading) {
    mainContent = <DashboardLoader
      {...localState}
      styles={DEFAULT_LOADER_STYLES}
    />
  }

  // Error view
  else if (localState.error) {
    mainContent = <DashboardLoader
      {...localState}
      styles={DEFAULT_LOADER_STYLES}
    />
  }

  // All bikes view
  else if (localState.view === 'all') {
    mainContent = <Fragment>
      <Box className='gbk-tabs vehicles-tabs' sx={{ typography: 'body1', m: '30px 0 0 0' }}>
        {/* Phone FAB */}
        <Box className="phone-fab">
          <Button className="thick-border " variant="outlined" onClick={e => setLocalState({ view: 'create' })}><Add /> Add Vehicle</Button>
        </Box>
        <BikesTable
          bikesList={bikesData}
          setParentState={(s: LocalState) => { setPendingAction(undefined); setLocalState(s); }}
          containerClass={'gbk-grid-parent no-fabs vehicles-grid'}
          setBikeActive={setBikeActive}
        />
      </Box >
    </Fragment>
  }

  // Individual bike view
  else if (localState.view === 'individual') {
    if (!localState.bikeData) {
      setLocalState({ error: true, message: 'Invalid order data', view: defaultView });
    } else {
      mainContent = (
        <BikeDetails
          hubBike={localState.bikeData}
          backFn={(reload?: boolean) => { setPendingAction(undefined); if (reload) return loadBikes(); if (bikeDetailsView) setLocalState({ view: defaultView, bikeData: undefined }) }}
          drawerWidth={props.drawerWidth}
          editMode={false}
          creating={false}
          initialAction={pendingAction}
        />
      )
    }
  }

  // Individual bike view
  else if (localState.view === 'create') {
    mainContent = (
      <BikeDetails
        hubBike={DefaultSliceData.DUMMY_BIKE}
        backFn={(reload?: boolean) => { if (reload) return loadBikes(); if (bikeDetailsView) setLocalState({ view: defaultView, bikeData: undefined }) }}
        drawerWidth={props.drawerWidth}
        editMode={true}
        creating={true}
      />
    )
  }

  // Default view
  else {
    mainContent = <div></div>;
  }

  // Return DOM
  return (
    <Box sx={{ mt: { xs: '50px', md: 0 }, ml: { xs: 0, md: `${props.drawerWidth}px` }, width: { xs: '100%', md: `calc(100% - ${props.drawerWidth}px)` }, height: '100%' }}>
      <div className={`gbk-tabcontent ${localState.view === 'all' ? '' : 'allow-scroll'}`}>

        {/* Add bike fab */}
        <Button
          onClick={e => setLocalState({ view: 'create' })}
          className='gbk-fab gbk-fab-top-right'
          sx={{ display: { xs: 'none', md: 'inline-flex' } }}
          variant='contained'
          size='large'
          color='primary'
          disabled={localState.view !== 'all'}
        >
          <Add /> Add Vehicle
        </Button>

        {/* Header */}
        <Box className='tab-header' sx={{ display: { xs: 'none', md: 'block' } }}>

          {/* Icon */}
          <div
            className={`tab-header-fab ${bikeDetailsView ? 'clickable' : ''}`}
            onClick={() => { if (bikeDetailsView) setLocalState({ view: defaultView, bikeData: undefined }) }} >
            {
              bikeDetailsView
                ? <ArrowBack fontSize="large" />
                : <MenuBookOutlined fontSize="large" />
            }
          </div>

          {/* Text */}
          <Typography>
            <span style={{ ...(bikeDetailsView && { color: Colors.primary }) }}>
              Vehicles
            </span>
            {localState.view === 'create' ? ' / Add Vehicle' : (bikeDetailsView ? ' / Vehicle Details' : null)}
          </Typography>
        </Box>

        {/* Main content */}
        {mainContent}

      </div >
    </Box >
  )

}